<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateBrokerCommissionControls from '../components/RealEstateBrokerCommissionControls.vue';
import RealEstateBrokerCommissionDesktopTable from '../components/RealEstateBrokerCommissionDesktopTable.vue';
import RealEstateBrokerCommissionMobileList from '../components/RealEstateBrokerCommissionMobileList.vue';
import RealEstateBrokerCommissionPagination from '../components/RealEstateBrokerCommissionPagination.vue';
import RealEstateBrokerOnboardingGate from '../components/RealEstateBrokerOnboardingGate.vue';
import RealEstateBrokerMobileNavigation from '../components/RealEstateBrokerMobileNavigation.vue';
import RealEstateBrokerRecentList from '../components/RealEstateBrokerRecentList.vue';
import RealEstateBrokerSummary from '../components/RealEstateBrokerSummary.vue';
import RealEstateBrokerProductionAccordion from '../components/RealEstateBrokerProductionAccordion.vue';
import RealEstateBrokerDesktopNavigation from '../components/RealEstateBrokerDesktopNavigation.vue';
import { useRealEstateBrokerDashboard } from '../composables/useRealEstateBrokerDashboard';
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const dashboard = useRealEstateBrokerDashboard({ notify: useAlert, t });
const greeting = computed(() =>
  t('REAL_ESTATE_BROKER_DASHBOARD.WELCOME', {
    name: currentUser.value?.name?.split(' ')?.[0] || '',
  })
);
const refreshLabel = computed(() => {
  if (dashboard.minutesSinceRefresh.value === null) return '';
  return t('REAL_ESTATE_BROKER_DASHBOARD.ACTIONS.REFRESHED_AT', {
    minutes: dashboard.minutesSinceRefresh.value,
  });
});
const isCommissionSection = computed(() => route.query.section === 'comissao');
const openCommission = installmentId =>
  router.push({
    name: 'operacao_imobiliaria_corretor_parcela',
    params: { accountId: route.params.accountId, installmentId },
  });
const openInvestments = () =>
  router.push({
    name: 'operacao_imobiliaria_corretor_investimentos',
    params: { accountId: route.params.accountId },
  });
const openProduction = () => {
  dashboard.selectStatus('liberado');
  router.push({
    name: 'operacao_imobiliaria_corretor',
    params: { accountId: route.params.accountId },
    query: { section: 'comissao' },
  });
};
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full bg-lais-bg-soft"
  >
    <RealEstateBrokerOnboardingGate />
    <LaisHeader
      :title="t('REAL_ESTATE_BROKER_DASHBOARD.TITLE')"
      :subtitle="greeting"
      :breadcrumb="[{ label: t('REAL_ESTATE_BROKER_DASHBOARD.TITLE') }]"
    >
      <template #actions>
        <LaisPrimaryButton
          icon="i-lucide-wallet"
          variant="ghost"
          @click="openInvestments"
        >
          {{ t('REAL_ESTATE_BROKER_INVESTMENT.TITLE') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-refresh-cw"
          variant="ghost"
          :is-loading="dashboard.loading.value"
          @click="dashboard.load"
        >
          {{ t('REAL_ESTATE_BROKER_DASHBOARD.ACTIONS.REFRESH') }}
        </LaisPrimaryButton>
        <p v-if="refreshLabel" class="text-xs text-lais-text-muted">
          {{ refreshLabel }}
        </p>
      </template>
    </LaisHeader>
    <div class="mx-auto flex w-full max-w-[1900px]">
      <RealEstateBrokerDesktopNavigation
        :commission-summary="dashboard.summary.value"
        :selected-commission="dashboard.status.value"
        @select-commission="dashboard.selectStatus"
      />
      <main
        class="flex min-w-0 flex-1 flex-col gap-5 px-4 pb-24 sm:px-6 lg:px-10 md:pb-8"
      >
        <LaisCard
          density="tight"
          :class="route.query.section === 'comissao' ? 'hidden md:block' : ''"
        >
          <p class="p-4 text-sm text-lais-text-body">
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.SUBTITLE') }}
          </p>
        </LaisCard>
        <RealEstateBrokerProductionAccordion
          v-if="!isCommissionSection"
          :loading="dashboard.loading.value"
          :summary="dashboard.summary.value"
          @view-production="openProduction"
        />
        <RealEstateBrokerSummary
          class="hidden md:grid"
          :loading="dashboard.loading.value"
          :summary="dashboard.summary.value"
          @view-production="openProduction"
        />
        <p
          v-if="dashboard.error.value"
          class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {{ dashboard.error.value }}
        </p>
        <div
          class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.72fr)]"
        >
          <section class="min-w-0 space-y-3">
            <RealEstateBrokerCommissionControls
              :status="dashboard.status.value"
              :summary="dashboard.summary.value"
              :filters="dashboard.filters.value"
              :order="dashboard.order.value"
              :addresses="dashboard.addresses.value"
              :loading="dashboard.loading.value"
              @select-status="dashboard.selectStatus"
              @apply-address="dashboard.applyAddress"
              @set-order="dashboard.setOrder"
            />
            <RealEstateBrokerCommissionDesktopTable
              :loading="dashboard.loading.value"
              :rows="dashboard.rows.value"
              @open="openCommission"
            />
            <RealEstateBrokerCommissionMobileList
              :loading="dashboard.loading.value"
              :rows="dashboard.rows.value"
              @open="openCommission"
            />
            <RealEstateBrokerCommissionPagination
              :page="dashboard.page.value"
              :total-pages="dashboard.totalPages.value"
              :visible-count="dashboard.rows.value.length"
              :total-count="dashboard.totalRows.value"
              @change="dashboard.changePage"
            />
          </section>
          <aside
            class="min-w-0 xl:col-span-2"
            :class="route.query.section === 'comissao' ? 'hidden md:block' : ''"
          >
            <RealEstateBrokerRecentList
              :loading="dashboard.loading.value"
              :rows="dashboard.recent.value"
              @open="openCommission"
            />
          </aside>
        </div>
      </main>
    </div>
    <RealEstateBrokerMobileNavigation />
  </LaisGamificationSurface>
</template>
